import { ImageSize } from 'types/isomorphic';
import modifiedSizeResolution from './modifiedSizeResolution';

/**
 * Which modified sizes a selected UltraPack entitles, by the pack's resolution
 * tier:
 *   - low resolution pack  -> only the Standard (1k) modified size
 *   - medium / high pack    -> all modified sizes (1k/2k/4k)
 *
 * `tier` is a SKU-derived 'low' | 'med' | 'hi' getter present on any
 * Agreement (see src/shared/models/agreement.ts) — derived purely from the SKU
 * pattern, not gated by agreement type; a non-matching or undefined tier (e.g.
 * Premium Access) is treated as unrestricted. Only the low tier narrows the
 * list.
 *
 * The Standard tier is identified via the shared `modifiedSizeResolution`
 * mapper (a low/1k sizeName resolves to '1k'), so the buy-card UI
 * (ModifiedBuyCard) and the GA4 download events (ModifiedDownloadButton /
 * ModifiedRedownloadButton) gate `asset_sizes` from a single source and cannot
 * drift. Tee-shirt (extend) sizes have no resolution and are never emitted by
 * ModifiedBuyCard's tier-gated download flow, so callers apply this only to the
 * resolution-based (fibo/pre-shot) sizes.
 */
const STANDARD_RESOLUTION = '1k';

const restrictModifiedSizesForPackTier = (
  modifiedSizes: ImageSize[],
  tier?: string
): ImageSize[] => {
  if (tier !== 'low') return modifiedSizes;
  const standardOnly = modifiedSizes.filter(
    (size) => modifiedSizeResolution(size.sizeName) === STANDARD_RESOLUTION
  );
  return standardOnly.length > 0 ? standardOnly : modifiedSizes.slice(0, 1);
};

export default restrictModifiedSizesForPackTier;
